import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useFilteredIncidentColumns } from '../hooks/useFilteredIncidentColumns'
import { useIncidentWithDisplayId } from '../hooks/useIncidentWithDisplayId'
import { useCanBulkAction } from '@/hooks/useBulkAction'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import {
  PaginationState,
  type RowSelectionState,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'
import { BiCheck, BiX } from 'react-icons/bi'
import { useDispatch, useSelector } from 'react-redux'

import { Box, useDisclosure } from '@chakra-ui/react'
import mixpanel from 'mixpanel-browser'

import { AiOperatorIcon } from '@hivewatch/dark'

import { ActionBar } from '@/components/layouts/ActionBar'
import {
  PageSizeSelector,
  ROW_HEIGHT,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
  TableRowsSkeleton,
} from '@/components/ui'
import { setIncidentsPageSize } from '@/redux/incidents/incidentsSlice'
import {
  selectIncidentDrawerNavigationContext,
  selectIsIncidentDrawerOpen,
  setIncidentDrawerNavigationContext,
} from '@/redux/ui/uiSlice'
import { AI_INCIDENT_SUMMARY_BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import { IncidentsListType } from '../types/types'
import { INCIDENT_TABLE_HEIGHT, formatTableData } from '../utils/helpers'
import { BulkActionModal } from './BulkActionModal'
import { SummarizeIncidentsModal } from './SummarizeIncidentsModal'

interface IncidentsTableIProps {
  incidents?: IncidentsListType[]
  totalIncidents?: number
  isLoading?: boolean
  paginationState: PaginationState
  setPaginationState: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  isPriorityQueueActive?: boolean
}

export const IncidentsTable = ({
  incidents,
  totalIncidents,
  isLoading = false,
  paginationState,
  setPaginationState,
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
  isPriorityQueueActive = false,
}: IncidentsTableIProps) => {
  // region State & Data management
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const pageCount = Math.ceil((totalIncidents ?? 0) / paginationState.pageSize)
  const { openIncident, setDisplayId, openAlertIncidentDrawer } =
    useOpenIncident()
  const { query } = useRouter()
  const isDrawerOpen = useSelector(selectIsIncidentDrawerOpen)
  const incidentDrawerNavigationContext = useSelector(
    selectIncidentDrawerNavigationContext
  )
  const { queryIncidentId } = useIncidentWithDisplayId({
    displayId: query?.displayId,
  })
  const tableRef = useRef()

  const {
    isOpen: isBulkModalOpen,
    onOpen: onBulkModalOpen,
    onClose: onBulkModalClose,
  } = useDisclosure()

  const {
    isOpen: isSummarizeModalOpen,
    onOpen: onSummarizeModalOpen,
    onClose: onSummarizeModalClose,
  } = useDisclosure()

  const isQuickReportWhiteListEnabled = useFeatureFlag(
    FeatureFlag.quickReportWhitelist
  )

  const isAiIncidentSummaryEnabled = useFeatureFlag(
    FeatureFlag.aiIncidentSummary
  )

  const tableData = formatTableData(incidents, isQuickReportWhiteListEnabled)
  const navigationIncidents = useMemo(
    () =>
      (incidents ?? [])
        .filter((incident) => Boolean(incident?.id && incident?.displayId))
        .map((incident) => ({
          id: incident.id,
          displayId: incident.displayId,
        })),
    [incidents]
  )

  const table = useReactTable({
    data: tableData,
    columns: useFilteredIncidentColumns({ isPriorityQueueActive }),
    getRowId: (row) => row.id,
    state: {
      pagination: paginationState,
      sorting,
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: !isPriorityQueueActive,
    pageCount,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (updater) => {
      setRowSelection({})
      setPaginationState(updater)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  })

  useEffect(() => {
    if (queryIncidentId) {
      dispatch(
        setIncidentDrawerNavigationContext({
          isFromIncidentsTable: true,
          incidents: navigationIncidents,
        })
      )
      openAlertIncidentDrawer(queryIncidentId)
    }
  }, [dispatch, openAlertIncidentDrawer, queryIncidentId])

  // We keep this because a refresh can clear drawer navigation context.
  // The drawer still opens from `displayId` in the URL, but prev/next loses the table context.
  // When incidents load again, we rebuild context so navigation can recover.
  useEffect(() => {
    if (
      !isDrawerOpen ||
      !incidentDrawerNavigationContext?.isFromIncidentsTable
    ) {
      return
    }

    dispatch(
      setIncidentDrawerNavigationContext({
        isFromIncidentsTable: true,
        incidents: navigationIncidents,
      })
    )
  }, [
    dispatch,
    isDrawerOpen,
    incidentDrawerNavigationContext?.isFromIncidentsTable,
    navigationIncidents,
  ])

  const onPageResize = (pageIndex: number) => {
    dispatch(setIncidentsPageSize(pageIndex))
  }

  // This ref keeps track of the previous drawer state for comparison
  const prevDrawerOpenState = useRef(isDrawerOpen)
  /**
   * Clear the URL param when the drawer is no longer open
   */
  useEffect(() => {
    if (!isDrawerOpen && prevDrawerOpenState.current) {
      setDisplayId(null)
    }
    prevDrawerOpenState.current = isDrawerOpen
  }, [isDrawerOpen, setDisplayId])

  const handleSelectItem = (id: string) => {
    const selectedIncident = incidents.find((inc) => inc.id === id)
    dispatch(
      setIncidentDrawerNavigationContext({
        isFromIncidentsTable: true,
        incidents: navigationIncidents,
      })
    )
    mixpanel.track('Button Press: Incident Row, Incidents View', {
      incident_id: selectedIncident?.displayId,
      incident_status: selectedIncident?.status,
      incident_type: selectedIncident?.status,
    })
    openIncident({
      incidentId: id,
      displayId: selectedIncident?.displayId,
    })
  }

  useEffect(() => {
    const selectedIds: string[] = table
      .getSelectedRowModel()
      .rows.map((row) => {
        return row.original.id
      })

    setSelectedIncidentIds(selectedIds)
  }, [table.getState().rowSelection])

  const [selectedIncidentIds, setSelectedIncidentIds] = useState([])

  const selectedIncidents = useMemo(
    () =>
      (incidents ?? [])
        .filter((inc) => selectedIncidentIds.includes(inc.id))
        .map((inc) => ({ id: inc.id, displayId: inc.displayId })),
    [incidents, selectedIncidentIds]
  )

  const shouldShowBulkAction: boolean = useCanBulkAction()
  // endregion

  return (
    <Box ref={tableRef}>
      {/* region Bulk resolve ActionBar */}
      {/* Summarize lives on the bulk-action bar, so it inherits `useCanBulkAction()`: a user
          without bulk-resolve permission cannot summarize even with the flag on. Deliberate for
          now — revisit if Summarize should be available to read-only roles. */}
      {selectedIncidentIds.length > 0 && shouldShowBulkAction && (
        <ActionBar
          extraButton={
            isAiIncidentSummaryEnabled
              ? {
                  icon: AiOperatorIcon,
                  text: t('incidents:summarize'),
                  onClick: () => {
                    trackEvent(AI_INCIDENT_SUMMARY_BUTTON_PRESS, {
                      incident_count: selectedIncidentIds.length,
                      view: 'legacy',
                    })
                    onSummarizeModalOpen()
                  },
                }
              : undefined
          }
          label={`${selectedIncidentIds.length} ${
            selectedIncidentIds.length === 1 ? 'incident' : 'incidents'
          } selected`}
          primaryButton={{
            icon: BiCheck,
            text: 'Bulk Resolve',
            onClick: onBulkModalOpen,
          }}
          secondaryButton={{
            icon: BiX,
            text: 'Clear All',
            onClick: () => {
              table.setRowSelection({})
            },
          }}
        />
      )}
      {/* endregion */}

      <Table
        tableContainerStyles={{
          maxH: INCIDENT_TABLE_HEIGHT,
          mb: '42px',
        }}
      >
        <TableHeader
          paddingX='16px'
          rowBgColor='#E1E9FC'
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#E1E9FC',
          }}
          table={table}
        />
        {/* region Table Body */}
        {isLoading ? (
          <TableRowsSkeleton
            columnCount={table.getVisibleLeafColumns().length}
            rowCount={paginationState.pageSize}
            rowHeight={`${ROW_HEIGHT}px`}
          />
        ) : (
          <TableBody
            borderBottomColor='border.100'
            onRowClick={handleSelectItem}
            paddingX='16px'
            paddingY='8px'
            rowModel={table.getRowModel()}
          />
        )}
        {/* endregion */}
        {/* region Table Footer */}
        <TableFooter
          onGoToPage={table.setPageIndex}
          onNextPage={table.nextPage}
          onPreviousPage={table.previousPage}
          pageCount={pageCount}
          pageIndex={paginationState.pageIndex}
          pageSize={paginationState.pageSize}
          rightContent={
            <PageSizeSelector
              onClick={onPageResize}
              pageSize={table.getState().pagination.pageSize}
              setPageSize={table.setPageSize}
              title='Incidents per page:'
            />
          }
          total={totalIncidents}
        />
        {/* endregion */}
      </Table>

      {/* region Bulk Action Modal */}
      <BulkActionModal
        incidentIds={selectedIncidentIds}
        isOpen={isBulkModalOpen}
        onClose={onBulkModalClose}
        onSuccess={() => {
          table.setRowSelection({})
        }}
      />
      {/* endregion */}

      {/* region Summarize Incidents Modal */}
      {isAiIncidentSummaryEnabled && (
        <SummarizeIncidentsModal
          incidents={selectedIncidents}
          isOpen={isSummarizeModalOpen}
          onClose={onSummarizeModalClose}
        />
      )}
      {/* endregion */}
    </Box>
  )
}
